import * as _ from 'lodash';

import * as VEX_REASONS from './vex';
import type { ExceptionIgnoreScope, ExceptionPackageScope } from './request';

export const STATUS = ['active', 'ignored'] as const;

export type Status = typeof STATUS[number];

export type StatusWithRemediated = Status | 'remediated';

/**
 * Used when comparing the issues between two revisions.
 */
export enum ChangeStatus {
  New = 'new',
  Remediated = 'remediated',
  Unchanged = 'unchanged',
}

export enum ContainerLayerKind {
  BaseLayer = 'baseLayer',
  OtherLayer = 'otherLayer',
}

export enum DependencyUsage {
  Direct = 'direct',
  Deep = 'deep',
}

export enum ContainerLayerUsage {
  Base = 'base',
  Other = 'other',
}

export enum Ticketed {
  Jira = 'jira',
  Custom = 'custom',
  HasTicket = 'has_ticket',
  NoTicket = 'no_ticket',
}

export enum Category {
  Licensing = 'licensing',
  Vulnerability = 'vulnerability',
  Quality = 'quality',
}

export enum IssueSource {
  Snippet = 'snippet',
  ManagedDependency = 'managed-dependency',
  VendoredDependency = 'vendored-dependency',
}

export enum VulnSeverity {
  Critical = 'critical',
  High = 'high',
  Medium = 'medium',
  Low = 'low',
  Unknown = 'unknown',
}

export enum SeveritySource {
  Standard = 'standard',
  Custom = 'custom',
}

export enum VulnHasFix {
  HasFix = 'has_fix',
  NoFix = 'no_fix',
}

export enum VulnEpssType {
  Score = 'score',
  Percentile = 'percentile',
}

export const VULN_UPGRADE_DISTANCE = [
  'none',
  'major',
  'minor',
  'patch',
  'unknown',
] as const;

export type VulnUpgradeDistance = (typeof VULN_UPGRADE_DISTANCE)[number];

export const EXPLOIT_MATURITY = ['has_known_exploit', 'no_known_exploit'] as const;

export type ExploitMaturity = (typeof EXPLOIT_MATURITY)[number];

export type EpssFilter = {
  type: VulnEpssType,
  min: number,
  max: number,
};

export const LICENSING_ISSUE_TYPE = {
  PolicyConflict: 'policy_conflict',
  PolicyFlag: 'policy_flag',
  UnlicensedDependency: 'unlicensed_dependency',
  UnconcludedDependency: 'unconcluded_dependency',
  MultiLicenseConcluded: 'multi_license_concluded',
} as const;

export const LICENSING_ISSUE_TYPES = [
  LICENSING_ISSUE_TYPE.PolicyConflict,
  LICENSING_ISSUE_TYPE.PolicyFlag,
  LICENSING_ISSUE_TYPE.UnlicensedDependency,
  LICENSING_ISSUE_TYPE.UnconcludedDependency,
  LICENSING_ISSUE_TYPE.MultiLicenseConcluded,
] as const;

export type LicensingIssueType = (typeof LICENSING_ISSUE_TYPES)[number];

export const LICENSING_ISSUE_TYPE_LABELS: Record<LicensingIssueType, string> = {
  policy_conflict: 'Denied',
  policy_flag: 'Flagged',
  unlicensed_dependency: 'Unlicensed',
  unconcluded_dependency: 'Unconcluded',
  multi_license_concluded: 'Concluded (Multi)',
};

export const LICENSING_IDENTIFICATION = ['declared', 'discovered'] as const;

export type LicensingIdentification = (typeof LICENSING_IDENTIFICATION)[number];

export const LICENSING_IDENTIFICATION_LABELS: Record<LicensingIdentification, string> = {
  declared: 'Declared',
  discovered: 'Discovered',
};

export const RISK_ISSUE_TYPE = {
  RiskAbandonware: 'risk_abandonware',
  RiskEmptyPackage: 'risk_empty-package',
  RiskNativeCode: 'risk_native-code',
} as const;

export const RISK_ISSUE_TYPES = [
  RISK_ISSUE_TYPE.RiskAbandonware,
  RISK_ISSUE_TYPE.RiskEmptyPackage,
  RISK_ISSUE_TYPE.RiskNativeCode,
] as const;

export type RiskIssueType = (typeof RISK_ISSUE_TYPES)[number];

export const QUALITY_ISSUE_TYPE = {
  OutdatedDependency: 'outdated_dependency',
  BlacklistedDependency: 'blacklisted_dependency',
} as const;

export const QUALITY_ISSUE_TYPES = [
  QUALITY_ISSUE_TYPE.OutdatedDependency,
  QUALITY_ISSUE_TYPE.BlacklistedDependency,
  ...RISK_ISSUE_TYPES,
] as const;

export type QualityIssueType = (typeof QUALITY_ISSUE_TYPES)[number];

export const QUALITY_ISSUE_TYPE_LABELS: Record<QualityIssueType, string> = {
  outdated_dependency: 'Outdated version',
  blacklisted_dependency: 'Package blocked',
  risk_abandonware: 'Abandonware',
  'risk_empty-package': 'Empty package',
  'risk_native-code': 'Native code',
};

export const VULN_ISSUE_TYPE = {
  Vulnerability: 'vulnerability',
  Malware: 'malware',
} as const;

export const VULN_ISSUE_TYPES = [VULN_ISSUE_TYPE.Vulnerability, VULN_ISSUE_TYPE.Malware] as const;

export type VulnIssueType = (typeof VULN_ISSUE_TYPES)[number];

export const VULN_ISSUE_TYPE_LABELS: Record<VulnIssueType, string> = {
  vulnerability: 'Vulnerability',
  malware: 'Malicious Dependency',
};

export const ISSUE_TYPES = [...LICENSING_ISSUE_TYPES, ...QUALITY_ISSUE_TYPES, ...VULN_ISSUE_TYPES] as const;
//  TODO: let's rename this to AnyIssueType
export type IssueType = (typeof ISSUE_TYPES)[number];

export const ISSUE_TYPE_TO_CATEGORY = {
  ...Object.fromEntries(LICENSING_ISSUE_TYPES.map((k) => [k, Category.Licensing])),
  ...Object.fromEntries(QUALITY_ISSUE_TYPES.map((k) => [k, Category.Quality])),
  ...Object.fromEntries(VULN_ISSUE_TYPES.map((k) => [k, Category.Vulnerability])),
} as Record<IssueType, Category>;

export const CATEGORY_TO_ISSUE_TYPES: Record<Category, readonly IssueType[]> = {
  [Category.Licensing]: LICENSING_ISSUE_TYPES,
  [Category.Quality]: QUALITY_ISSUE_TYPES,
  [Category.Vulnerability]: VULN_ISSUE_TYPES,
};

export enum IssueScopeKind {
  Global = 'global',
  Project = 'project',
  ReleaseGroup = 'releaseGroup',
}

export type GlobalScope = {
  type: IssueScopeKind.Global;
};
export type ProjectScope = {
  type: IssueScopeKind.Project;
  id: string;
  revision?: string; // This should just be the revision hash, aka the part after the `$` symbol.
  revisionScanId?: number;
  /**
   * The revision we're comparing to and which set of issues we're looking for.
   * This field should only exist when we are comparing the issues between two revisions.
   */
  compareTo?: {
    /**
     * The revision **value** of the revision we're comparing too. ie the part after the `$` symbol.
     */
    revision: string;
    /**
     * Which set of issues are we looking for.
     */
    changeStatus: ChangeStatus;
  }
};
export type ReleaseGroupScope = {
  type: IssueScopeKind.ReleaseGroup;
  id: string;
  release: number;
  releaseScanId?: number;
  /**
   * The release we're comparing to and which set of issues we're looking for.
   * This field should only exist when we are comparing the issues between two releases.
   */
  compareTo?: {
    /**
     * The release ID of the release we're comparing to.
     */
    release: number;
    /**
     * Which set of issues are we looking for.
     */
    changeStatus: ChangeStatus;
  };
};

export type Scope = GlobalScope | ProjectScope | ReleaseGroupScope;

/**
 * In accordance with VEX: https://www.cisa.gov/sites/default/files/publications/VEX_Status_Justification_Jun22.pdf
 * These represent the possible reasons a vulnerability is marked as NOT AFFECTED
 */
export const VexReasons = Object.freeze({
  ComponentNotPresent: VEX_REASONS.COMPONENT_NOT_PRESENT,
  InlineMitigationsAlreadyExist: VEX_REASONS.INLINE_MITIGATIONS_ALREADY_EXIST,
  VulnerableCodeCannotBeControlled: VEX_REASONS.VULNERABLE_CODE_CANNOT_BE_CONTROLLED,
  VulnerableCodeNotInExecutePath: VEX_REASONS.VULNERABLE_CODE_NOT_IN_EXECUTE_PATH,
  VulnerableCodeNotPresent: VEX_REASONS.VULNERABLE_CODE_NOT_PRESENT,
});

export type VexReasonKeys = keyof typeof VexReasons;

export type VexReason = (typeof VexReasons)[VexReasonKeys];

export const AutoIgnoreReasons = Object.freeze({
  NonConcluded: 'non_concluded_policy_auto_ignore',
});

type AutoIgnoreReasonKeys = keyof typeof AutoIgnoreReasons;

export type AutoIgnoreReason = (typeof AutoIgnoreReasons)[AutoIgnoreReasonKeys];

/**
 * maps to a row in the ResolutionsReasons table, and is a superset of
 * vex reasons. TODO: should these live with the model definitions?
 */
export const InternalResolutionReasons = Object.freeze({
  ...VexReasons,
  IncorrectDataFound: 'incorrect_data_found',
  UnderInvestigation: 'Under_investigation',
  Fixed: 'Fixed',
  ...AutoIgnoreReasons,
});

/**
 * Resolution reasons that are exposed to the frontend.
 * Note that any reasons that are present in the InternalResolutionReasons
 * but should not be exposed to the frontend must be omitted from this object.
 */
export const ResolutionReasons = Object.freeze(
  _.omit(InternalResolutionReasons, AutoIgnoreReasons.NonConcluded),
);

type ResolutionReasonKeys = keyof typeof ResolutionReasons;

export type ResolutionReason = (typeof ResolutionReasons)[ResolutionReasonKeys];

type InternalResolutionReasonKeys = keyof typeof InternalResolutionReasons;
export type InternalResolutionReason = (typeof InternalResolutionReasons)[InternalResolutionReasonKeys];

export const InternalResolutionReasonDescriptions: Record<ResolutionReason, string> = Object.freeze({
  [InternalResolutionReasons.Fixed]: 'Fixed',
  [InternalResolutionReasons.UnderInvestigation]: 'Under Investigation',
  [InternalResolutionReasons.ComponentNotPresent]: 'Not Affected: Component Not Present',
  [InternalResolutionReasons.IncorrectDataFound]: 'Not Affected: Incorrect Data Found',
  [InternalResolutionReasons.InlineMitigationsAlreadyExist]: 'Not Affected: Inline Mitigations Already Exist',
  [InternalResolutionReasons.VulnerableCodeCannotBeControlled]: 'Not Affected: Vulnerable Code Cannot Be Controlled by an Adversary',
  [InternalResolutionReasons.VulnerableCodeNotInExecutePath]: 'Not Affected: Vulnerable Code Not in Execute Path',
  [InternalResolutionReasons.VulnerableCodeNotPresent]: 'Not Affected: Vulnerable Code Not Present',
  [InternalResolutionReasons.NonConcluded]: 'Ignored automatically by the Intelligent Auto Ignore policy setting',
});

export const AutoIgnoreReasonDescriptions = Object.freeze(
  _.pick(InternalResolutionReasonDescriptions, Object.values(AutoIgnoreReasons)),
);

type AutoIgnoreReasonDescriptionKeys = keyof typeof AutoIgnoreReasonDescriptions;
export type AutoIgnoreReasonDescription = (typeof AutoIgnoreReasonDescriptions)[AutoIgnoreReasonDescriptionKeys];

/**
 * Takes in an ignore reason name, like 'non_concluded_policy_auto_ignore', and returns true if it matches any of the auto-ignore reasons.
 */
export const isAutoIgnoreReason = (reason: string): reason is AutoIgnoreReason => {
  return Object.values(AutoIgnoreReasons).includes(reason as AutoIgnoreReason);
};

/**
 * Takes in an ignore reason message and returns true if it matches any of the auto-ignore reason descriptions.
 */
export const isAutoIgnoreReasonDescription = (reason: string): reason is AutoIgnoreReasonDescription => {
  return Object.values(AutoIgnoreReasonDescriptions).includes(reason);
};

/**
 * Resolution reasons that are exposed to the frontend.
 * Note that any reasons that are present in the InternalResolutionReasons
 * but should not be exposed to the frontend must be omitted from this object.
 */
export const ResolutionReasonDescriptions: Record<ResolutionReason, string> = Object.freeze(
  // We're type casting this because we're omitting a key from the InternalResolutionReasonDescriptions object
  // using lodash's omit function, and that type isn't being correctly inferred down the line.
  _.omit(InternalResolutionReasonDescriptions, InternalResolutionReasons.NonConcluded) as Record<
    ResolutionReason,
    string
  >,
);

/**
 * All of the different ways a vuln can be ignored
 */
export const VulnIgnoreReasons = Object.freeze({
  ...ResolutionReasons,
  Other: 'other',
});

type VulnIgnoreKeys = keyof typeof VulnIgnoreReasons;

export type VulnIgnoreReason = (typeof VulnIgnoreReasons)[VulnIgnoreKeys];

export const VulnIgnoreReasonDescriptions: Record<VulnIgnoreReason, string> = {
  ...ResolutionReasonDescriptions,
  [VulnIgnoreReasons.Other]: 'Not Affected: Other',
};

export enum IgnoredKind {
  Manual = 'manual',
  IssueException = 'issueException',
}

export type ManuallyIgnored = {
  ignoreType: IgnoredKind.Manual;
};

export type ExceptionIgnored = {
  ignoreType: IgnoredKind.IssueException;
  issueExceptionId: number;
  ignoreScope: ExceptionIgnoreScope;
  packageScope: ExceptionPackageScope | null;
  revisionLocator: string | null;
};

export type IgnoredKindMetadata = ManuallyIgnored | ExceptionIgnored;

export const exceptionsEnabledCategories = [Category.Licensing, Category.Vulnerability, Category.Quality];

export type Grouping = 'issue' | 'revision';

export type ExportType = 'jira' | 'custom';

export const CVSS_ATTACK_VECTOR = ['network', 'adjacent', 'local', 'physical'] as const;
export type CvssAttackVector = (typeof CVSS_ATTACK_VECTOR)[number];

export const CVSS_ATTACK_COMPLEXITY = ['low', 'high'] as const;
export type CvssAttackComplexity = (typeof CVSS_ATTACK_COMPLEXITY)[number];

export const CVSS_PRIVILEGES_REQUIRED = ['none', 'low', 'high'] as const;
export type CvssPrivilegesRequired = (typeof CVSS_PRIVILEGES_REQUIRED)[number];
